/**
 * Timeline Utilities
 * Helper functions for timeline calculations and positioning
 */

import { activities, companies } from '../../../../db';
import { TimelineMarker, TimelineBounds, UnifiedProject, TimeScale } from './types';

export const getTimelineBounds = (
  sortedProjects: UnifiedProject[], 
  timeScale: TimeScale
): TimelineBounds => {
  if (!sortedProjects.length) {
    return {
      startDate: new Date(),
      endDate: new Date(),
      totalDuration: 0
    };
  }

  const startDate = new Date(Math.min(...sortedProjects.map(p => new Date(p.startDate || '1970-01-01').getTime())));
  const endDate = new Date(Math.max(...sortedProjects.map(p => {
    const endTime = p.endDate && p.endDate !== 'Present' ? new Date(p.endDate).getTime() : Date.now();
    return endTime;
  })));

  // Add padding based on time scale
  const paddingMs = timeScale === 'month' ? 30 * 24 * 60 * 60 * 1000 * 3 : // 3 months
                   timeScale === 'quarter' ? 30 * 24 * 60 * 60 * 1000 * 6 : // 6 months (2 quarters)
                   365 * 24 * 60 * 60 * 1000; // 1 year

  startDate.setTime(startDate.getTime() - paddingMs);
  endDate.setTime(endDate.getTime() + paddingMs);

  return {
    startDate,
    endDate,
    totalDuration: endDate.getTime() - startDate.getTime()
  };
};

export const generateTimelineMarkers = (
  bounds: TimelineBounds,
  containerWidth: number,
  zoomLevel: number,
  timeScale: TimeScale
): TimelineMarker[] => {
  const markers: TimelineMarker[] = [];
  const { startDate, endDate, totalDuration } = bounds;
  
  // For timeline view, ensure we use a minimum width that provides good spacing
  const baseWidth = Math.max(containerWidth, 1200); // Minimum 1200px for good timeline spacing
  const effectiveWidth = baseWidth * zoomLevel; // Remove Math.max to allow zooming out below 100%
  const margin = 40;
  const availableWidth = effectiveWidth - (2 * margin);

  if (timeScale === 'month') {
    const current = new Date(startDate.getFullYear(), startDate.getMonth(), 1);
    
    while (current <= endDate) {
      const position = ((current.getTime() - startDate.getTime()) / totalDuration) * availableWidth;
      const isYearStart = current.getMonth() === 0;
      
      markers.push({
        position,
        label: isYearStart
          ? current.getFullYear().toString()
          : current.toLocaleDateString('en-US', { month: 'short' }),
        date: new Date(current),
        type: isYearStart ? 'major' : 'minor'
      });
      
      current.setMonth(current.getMonth() + 1);
    }
  } else if (timeScale === 'quarter') {
    const current = new Date(startDate.getFullYear(), Math.floor(startDate.getMonth() / 3) * 3, 1);
    
    while (current <= endDate) {
      const position = ((current.getTime() - startDate.getTime()) / totalDuration) * availableWidth;
      const quarter = Math.floor(current.getMonth() / 3) + 1;
      const isYearStart = current.getMonth() === 0;
      
      markers.push({
        position,
        label: isYearStart
          ? current.getFullYear().toString()
          : `Q${quarter}`,
        date: new Date(current),
        type: isYearStart ? 'major' : 'minor'
      });
      
      current.setMonth(current.getMonth() + 3);
    }
  } else { // year
    const current = new Date(startDate.getFullYear(), 0, 1);
    
    while (current <= endDate) {
      const position = ((current.getTime() - startDate.getTime()) / totalDuration) * availableWidth;
      
      markers.push({
        position,
        label: current.getFullYear().toString(),
        date: new Date(current),
        type: 'major'
      });
      
      current.setFullYear(current.getFullYear() + 1);
    }
  }

  return markers;
};

export const generateActivityMarkers = (
  bounds: TimelineBounds,
  sortedProjects: UnifiedProject[],
  containerWidth: number,
  zoomLevel: number
): Array<{
  position: number;
  activityId: number;
  companyId: number;
  companyName: string;
  companyLogo: string;
  startDate: Date;
  title: string;
}> => {
  const activityMarkers: Array<{
    position: number;
    activityId: number;
    companyId: number;
    companyName: string;
    companyLogo: string;
    startDate: Date;
    title: string;
  }> = [];
  
  const { startDate, endDate, totalDuration } = bounds;
  const baseWidth = Math.max(containerWidth, 1200);
  const effectiveWidth = baseWidth * zoomLevel; // Remove Math.max to allow zooming out below 100%
  const margin = 40;
  const availableWidth = effectiveWidth - (2 * margin);

  // Get unique activities from projects
  const uniqueActivities = new Map<number, {
    activityId: number;
    companyId: number;
    companyName: string;
    companyLogo: string;
    startDate: Date;
    title: string;
  }>();

  sortedProjects.forEach(project => {
    if (project.currentActivityId) {
      const activity = activities.find(a => a.id === project.currentActivityId);
      if (activity) {
        const company = companies.find(c => c.id === activity.companyId);
        if (company && !uniqueActivities.has(activity.id)) {
          const activityStartDate = new Date(activity.startDate);
          
          // Only include activities that fall within our timeline bounds
          if (activityStartDate >= startDate && activityStartDate <= endDate) {
            uniqueActivities.set(activity.id, {
              activityId: activity.id,
              companyId: company.id,
              companyName: company.name,
              companyLogo: company.logoPath || '',
              startDate: activityStartDate,
              title: activity.title
            });
          }
        }
      }
    }
  });

  // Convert to array and calculate positions
  Array.from(uniqueActivities.values()).forEach(activityInfo => {
    const position = ((activityInfo.startDate.getTime() - startDate.getTime()) / totalDuration) * availableWidth;
    activityMarkers.push({
      ...activityInfo,
      position
    });
  });

  return activityMarkers;
};

// Helper function to check if two projects overlap in time
export const projectsOverlap = (projectA: UnifiedProject, projectB: UnifiedProject): boolean => {
  const startA = new Date(projectA.startDate || '1970-01-01').getTime();
  const endA = projectA.endDate && projectA.endDate !== 'Present' 
    ? new Date(projectA.endDate).getTime() 
    : Date.now();
  
  const startB = new Date(projectB.startDate || '1970-01-01').getTime();
  const endB = projectB.endDate && projectB.endDate !== 'Present' 
    ? new Date(projectB.endDate).getTime() 
    : Date.now();
  
  // Projects overlap if one starts before the other ends
  return startA < endB && startB < endA;
};

// Assign projects to lanes to prevent overlaps
export const assignLanes = (projects: UnifiedProject[]): number[] => {
  const lanes: number[] = new Array(projects.length).fill(0);
  const laneTracks: { endTime: number; projectIndex: number }[][] = [];

  projects.forEach((project, index) => {
    const startTime = new Date(project.startDate || '1970-01-01').getTime();
    const endTime = project.endDate && project.endDate !== 'Present' 
      ? new Date(project.endDate).getTime() 
      : Date.now();

    // Find the first available lane
    let assignedLane = 0;
    
    while (assignedLane < laneTracks.length) {
      // Check if this lane has any conflicting projects
      const hasConflict = laneTracks[assignedLane].some(track => 
        track.endTime > startTime && 
        projects.findIndex(p => p === project) !== track.projectIndex
      );
      
      if (!hasConflict) {
        break;
      }
      assignedLane++;
    }

    // Create new lane if needed
    if (assignedLane >= laneTracks.length) {
      laneTracks.push([]);
    }

    // Assign project to lane and clean up expired projects from lane
    laneTracks[assignedLane] = laneTracks[assignedLane].filter(track => track.endTime > startTime);
    laneTracks[assignedLane].push({ endTime, projectIndex: index });
    lanes[index] = assignedLane;
  });

  return lanes;
};